/**
 * The notice a tab shows when its URL account is not the session's account
 * (ADR-0302, Decision 4). It lies OVER the page, which stays mounted: the
 * transport is stopped, so the page cannot act, and switching back keeps what
 * the user typed.
 */

import { Button, Group, Overlay, Paper, Stack, Text, Title } from "@mantine/core";
import { IconArrowsExchange } from "@tabler/icons-react";

export type MismatchKind =
  /** Another tab switched the session away from this tab's account. */
  | "switched"
  /** This tab was opened on a link to an account the session is not on. */
  | "arrival"
  /** Another tab signed in as a different user. */
  | "other-user"
  /** Another tab signed out. */
  | "signed-out";

type Props = {
  kind: MismatchKind;
  /** Name of the account in this tab's URL. */
  tabAccountName: string;
  /** Name of the account the session is on, when known. */
  sessionAccountName: string | null;
  busy: boolean;
  onUseSession: () => void;
  onUseTab: () => void;
  onSignIn: () => void;
};

export function AccountMismatchGate({
  kind,
  tabAccountName,
  sessionAccountName,
  busy,
  onUseSession,
  onUseTab,
  onSignIn,
}: Props) {
  const session = sessionAccountName ?? "another account";

  let title: string;
  let body: React.ReactNode;
  let actions: React.ReactNode;

  if (kind === "signed-out" || kind === "other-user") {
    title = kind === "signed-out" ? "You signed out in another tab" : "You signed in as another user in another tab";
    body = (
      <Text size="sm" c="dimmed">
        This tab was showing <b>{tabAccountName}</b>. Sign in again to continue.
      </Text>
    );
    actions = (
      <Button onClick={onSignIn} loading={busy}>
        {kind === "signed-out" ? "Sign in" : "Continue"}
      </Button>
    );
  } else if (kind === "arrival") {
    title = `This link belongs to ${tabAccountName}`;
    body = (
      <Text size="sm" c="dimmed">
        You're currently working in <b>{session}</b>. Opening it switches your other tabs to{" "}
        <b>{tabAccountName}</b> too.
      </Text>
    );
    actions = (
      <>
        <Button variant="default" onClick={onUseSession} disabled={busy}>
          Stay in {session}
        </Button>
        <Button onClick={onUseTab} loading={busy}>
          Open in {tabAccountName}
        </Button>
      </>
    );
  } else {
    title = `You're now working in ${session}`;
    body = (
      <Text size="sm" c="dimmed">
        You switched accounts in another tab. This tab was showing <b>{tabAccountName}</b>. Continuing
        in {session} leaves this page; switching back also switches your other tabs.
      </Text>
    );
    actions = (
      <>
        <Button variant="default" onClick={onUseTab} loading={busy}>
          Switch back to {tabAccountName}
        </Button>
        <Button onClick={onUseSession} disabled={busy}>
          Continue in {session}
        </Button>
      </>
    );
  }

  return (
    <Overlay fixed zIndex={1000} backgroundOpacity={0.55} blur={2} center>
      <Paper
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="account-mismatch-title"
        shadow="lg"
        radius="md"
        p="xl"
        maw={480}
        mx="md"
      >
        <Stack gap="md">
          <Group gap="sm" wrap="nowrap">
            <IconArrowsExchange size={22} />
            <Title order={4} id="account-mismatch-title">
              {title}
            </Title>
          </Group>
          {body}
          <Group justify="flex-end" gap="sm">
            {actions}
          </Group>
        </Stack>
      </Paper>
    </Overlay>
  );
}
